import React, { useState } from 'react';
import { Car, FileText, Key, Camera, ArrowRight, Zap, Shield, Eye, Wrench, X, Loader } from 'lucide-react';
import { createInspection, uploadPhoto, savePhotoRecord, logAuditEvent } from '../lib/supabase';
import CameraCapture from './CameraCapture';

const InspectionWizard = ({ currentStep, inspectionData, onStepComplete, onNext, onPrevious }) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraConfig, setCameraConfig] = useState(null);
  const [uploadingField, setUploadingField] = useState(null);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openCamera = (field, label, instructions) => {
    setCameraConfig({ field, label, instructions });
    setCameraOpen(true);
  };

  const handlePhotoCapture = async (blobUrl) => {
    setCameraOpen(false);
    const field = cameraConfig.field;
    setUploadingField(field);

    try {
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      URL.revokeObjectURL(blobUrl);

      const inspectionId = inspectionData.inspectionId || 'unsaved';
      const fileName = `${field}_${Date.now()}.jpg`;
      const storagePath = `${inspectionId}/${currentStep}/${fileName}`;

      const publicUrl = await uploadPhoto(blob, storagePath);

      if (inspectionData.inspectionId) {
        await savePhotoRecord(inspectionData.inspectionId, currentStep, publicUrl, fileName);
      }

      setFormData(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), publicUrl],
      }));
      logAuditEvent({
        inspectionId: inspectionData.inspectionId,
        eventType: 'photo_added',
        step: currentStep,
        field,
        newValue: { url: publicUrl, fileName },
        metadata: { storagePath },
      });
    } catch (err) {
      console.error('Photo upload failed:', err);
      alert('Photo upload failed. Please try again.');
    } finally {
      setUploadingField(null);
      setCameraConfig(null);
    }
  };

  // Reusable photo capture button with thumbnail strip
  const PhotoCapture = ({ field, label, instructions }) => {
    const photos = formData[field] || [];
    const isUploading = uploadingField === field;
    return (
      <div className="photo-capture-section">
        {photos.length > 0 && (
          <div className="photo-grid" style={{ marginBottom: '0.75rem' }}>
            {photos.map((url, i) => (
              <div key={i} className="photo-item" style={{ position: 'relative' }}>
                <img src={url} alt={`${label} ${i + 1}`} />
                <button
                  type="button"
                  onClick={() => updateFormData(field, photos.filter((_, idx) => idx !== i))}
                  style={{
                    position: 'absolute', top: 4, right: 4,
                    background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%',
                    width: 22, height: 22, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', cursor: 'pointer', color: 'white', padding: 0,
                  }}
                >
                  <X style={{ width: 12, height: 12 }} />
                </button>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          className="photo-capture-button"
          onClick={() => openCamera(field, label, instructions || `Take a clear photo of ${label}`)}
          disabled={isUploading}
        >
          {isUploading
            ? <><Loader className="w-5 h-5" style={{ animation: 'spin 1s linear infinite' }} /> Uploading...</>
            : <><Camera className="w-6 h-6" /> {photos.length > 0 ? `Add Another ${label} Photo` : `Take ${label} Photo`}</>
          }
        </button>
      </div>
    );
  };

  // Pass / Fail / N/A selector
  const ConditionSelector = ({ field, label }) => (
    <div className="counter-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.5rem' }}>
      <span className="form-label" style={{ margin: 0 }}>{label}</span>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        {['pass', 'fail', 'na'].map(val => (
          <button
            key={val}
            type="button"
            onClick={() => updateFormData(field, val)}
            style={{
              flex: 1, padding: '0.5rem',
              border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
              background: formData[field] === val
                ? val === 'pass' ? '#166534' : val === 'fail' ? '#dc2626' : '#64748b'
                : 'var(--surface)',
              color: formData[field] === val ? 'white' : 'var(--text-secondary)',
              fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
              textTransform: 'uppercase', transition: 'all 0.15s ease', fontFamily: 'inherit',
            }}
          >
            {val === 'na' ? 'N/A' : val}
          </button>
        ))}
      </div>
    </div>
  );

  // Checkbox item
  const ChecklistItem = ({ field, label, description }) => (
    <label className="checkbox-label">
      <input
        type="checkbox"
        checked={formData[field] || false}
        onChange={(e) => updateFormData(field, e.target.checked)}
      />
      <span className="checkmark"></span>
      <span>
        <span style={{ display: 'block', fontWeight: 600 }}>{label}</span>
        {description && <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 400 }}>{description}</span>}
      </span>
    </label>
  );

  const handleContinue = async () => {
    setIsLoading(true);
    try {
      if (currentStep === 'vehicle-info' && !inspectionData.inspectionId) {
        const newInspection = await createInspection(formData);
        onStepComplete({ vehicleInfo: formData, inspectionId: newInspection.id });
      } else {
        const stepKey = currentStep.replace('-', '_') + '_data';
        onStepComplete({ [stepKey]: formData });
      }
    } catch (error) {
      console.error('Error saving step:', error);
      alert('Error saving data. Please try again.');
    } finally {
      setIsLoading(false);
      setFormData({});
    }
  };

  // ── Step 1: Vehicle Info ──────────────────────────────────────────────
  const renderVehicleInfoStep = () => (
    <div className="step-content fade-in">
      <div className="step-header">
        <Car className="w-8 h-8 text-blue-600" />
        <h2>Vehicle Information</h2>
        <p>Enter the basic vehicle details to begin inspection</p>
      </div>
      <div className="form-container">
        <div className="form-group">
          <label className="form-label">VIN Number</label>
          <input type="text" className="form-input" placeholder="Enter 17-character VIN"
            value={formData.vin || ''} maxLength={17}
            onChange={(e) => updateFormData('vin', e.target.value.toUpperCase())} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Year</label>
            <input type="number" className="form-input" placeholder="2023"
              value={formData.year || ''} min="1900" max="2030"
              onChange={(e) => updateFormData('year', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Make</label>
            <input type="text" className="form-input" placeholder="Chevrolet"
              value={formData.make || ''}
              onChange={(e) => updateFormData('make', e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Model</label>
          <input type="text" className="form-input" placeholder="Blazer"
            value={formData.model || ''}
            onChange={(e) => updateFormData('model', e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Asset ID</label>
            <input type="text" className="form-input" placeholder="211"
              value={formData.assetId || ''}
              onChange={(e) => updateFormData('assetId', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Location</label>
            <input type="text" className="form-input" placeholder="MGE PMF"
              value={formData.location || ''}
              onChange={(e) => updateFormData('location', e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Odometer Reading</label>
          <input type="number" className="form-input" placeholder="28771"
            value={formData.odometer || ''}
            onChange={(e) => updateFormData('odometer', e.target.value)} />
        </div>
      </div>
      <button onClick={handleContinue} className="btn btn-primary full-width"
        disabled={!formData.vin || formData.vin.length !== 17 || isLoading}>
        {isLoading ? 'Saving...' : 'Continue to BOL'} <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  );

  // ── Step 2: BOL ───────────────────────────────────────────────────────
  const renderBOLStep = () => (
    <div className="step-content fade-in">
      <div className="step-header">
        <FileText className="w-8 h-8 text-blue-600" />
        <h2>BOL Documentation</h2>
        <p>Check and photograph the Bill of Lading</p>
      </div>
      <div className="instruction-card">
        <div className="instruction-icon"><Camera className="w-6 h-6 text-blue-600" /></div>
        <div>
          <h3>Photo Instructions</h3>
          <p>Take a clear photo of the complete Bill of Lading document</p>
        </div>
      </div>
      <div className="form-container">
        <div className="checkbox-group">
          <label className="checkbox-label">
            <input type="checkbox" checked={formData.bolProvided || false}
              onChange={(e) => updateFormData('bolProvided', e.target.checked)} />
            <span className="checkmark"></span>
            Driver provided BOL
          </label>
        </div>
        {formData.bolProvided && (
          <div className="slide-up">
            <PhotoCapture field="bol_photos" label="BOL Document"
              instructions="Take a clear, well-lit photo of the full Bill of Lading" />
          </div>
        )}
      </div>
      <button onClick={handleContinue} className="btn btn-primary full-width"
        disabled={!formData.bolProvided || isLoading}>
        {isLoading ? 'Saving...' : 'Continue to Keys & FOBs'} <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  );

  // ── Step 3: Keys ──────────────────────────────────────────────────────
  const renderKeysStep = () => (
    <div className="step-content fade-in">
      <div className="step-header">
        <Key className="w-8 h-8 text-blue-600" />
        <h2>Keys & FOBs</h2>
        <p>Count and photograph all keys and remote fobs</p>
      </div>
      <div className="instruction-card">
        <div className="instruction-icon"><Camera className="w-6 h-6 text-blue-600" /></div>
        <div>
          <h3>Photo Instructions</h3>
          <p>Arrange all keys and FOBs together for one clear photo</p>
        </div>
      </div>
      <div className="form-container">
        <div className="counter-group">
          <div className="counter-item">
            <label className="form-label">Mechanical Keys</label>
            <div className="counter-control">
              <button type="button" className="counter-btn"
                onClick={() => updateFormData('mechanicalKeys', Math.max(0, (formData.mechanicalKeys || 0) - 1))}>−</button>
              <span className="counter-value">{formData.mechanicalKeys || 0}</span>
              <button type="button" className="counter-btn"
                onClick={() => updateFormData('mechanicalKeys', (formData.mechanicalKeys || 0) + 1)}>+</button>
            </div>
          </div>
          <div className="counter-item">
            <label className="form-label">Key FOBs</label>
            <div className="counter-control">
              <button type="button" className="counter-btn"
                onClick={() => updateFormData('keyFobs', Math.max(0, (formData.keyFobs || 0) - 1))}>−</button>
              <span className="counter-value">{formData.keyFobs || 0}</span>
              <button type="button" className="counter-btn"
                onClick={() => updateFormData('keyFobs', (formData.keyFobs || 0) + 1)}>+</button>
            </div>
          </div>
        </div>
        <PhotoCapture field="keys_photos" label="Keys & FOBs"
          instructions="Lay all keys and FOBs flat and photograph together" />
      </div>
      <button onClick={handleContinue} className="btn btn-primary full-width" disabled={isLoading}>
        {isLoading ? 'Saving...' : 'Continue to Vehicle Function'} <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  );

  // ── Step 4: Vehicle Function ──────────────────────────────────────────
  const renderFunctionStep = () => (
    <div className="step-content fade-in">
      <div className="step-header">
        <Zap className="w-8 h-8 text-blue-600" />
        <h2>Vehicle Function</h2>
        <p>Test all primary vehicle systems and functions</p>
      </div>
      <div className="form-container">
        <div className="instruction-card">
          <div className="instruction-icon"><Zap className="w-6 h-6 text-blue-600" /></div>
          <div>
            <h3>Testing Instructions</h3>
            <p>Start the vehicle and test each system. Mark Pass, Fail, or N/A for each item.</p>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <p className="form-label">Engine & Starting</p>
          <ConditionSelector field="starts" label="Vehicle starts" />
          <ConditionSelector field="idles" label="Idles smoothly (no rough idle, no warning lights)" />
          <ConditionSelector field="transmission" label="Transmission shifts correctly" />
          <p className="form-label" style={{ marginTop: '0.5rem' }}>Lights</p>
          <ConditionSelector field="headlights" label="Headlights (low and high beam)" />
          <ConditionSelector field="taillights" label="Taillights and brake lights" />
          <ConditionSelector field="turnsignals" label="Turn signals (front and rear)" />
          <ConditionSelector field="hazards" label="Hazard lights" />
          <ConditionSelector field="reverselights" label="Reverse lights" />
          <p className="form-label" style={{ marginTop: '0.5rem' }}>Comfort & Controls</p>
          <ConditionSelector field="ac" label="Air conditioning" />
          <ConditionSelector field="heat" label="Heat / defrost" />
          <ConditionSelector field="wipers" label="Windshield wipers and washers" />
          <ConditionSelector field="horn" label="Horn" />
          <ConditionSelector field="windows" label="Power windows (all)" />
          <ConditionSelector field="mirrors" label="Power mirrors" />
        </div>
        <div className="form-group">
          <label className="form-label">Function Notes</label>
          <textarea className="form-input" rows={3}
            placeholder="Note any issues, warning lights, or unusual behavior..."
            value={formData.functionNotes || ''}
            onChange={(e) => updateFormData('functionNotes', e.target.value)}
            style={{ resize: 'vertical' }} />
        </div>
      </div>
      <button onClick={handleContinue} className="btn btn-primary full-width"
        disabled={isLoading || !formData.starts}>
        {isLoading ? 'Saving...' : 'Continue to Documentation'} <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  );

  // ── Step 5: Documentation ─────────────────────────────────────────────
  const renderDocumentationStep = () => (
    <div className="step-content fade-in">
      <div className="step-header">
        <Shield className="w-8 h-8 text-blue-600" />
        <h2>Documentation</h2>
        <p>Verify all required vehicle documents are present</p>
      </div>
      <div className="form-container">
        <div className="instruction-card">
          <div className="instruction-icon"><Camera className="w-6 h-6 text-blue-600" /></div>
          <div>
            <h3>Photo Instructions</h3>
            <p>Photograph each document clearly. Ensure all text is legible.</p>
          </div>
        </div>
        <div className="checkbox-group">
          <ChecklistItem field="licensePlatePresent" label="License plate present" description="Front and/or rear as required by state" />
          <ChecklistItem field="registrationPresent" label="Registration present" description="Current and not expired" />
          <ChecklistItem field="insurancePresent" label="Insurance card present" description="Current policy documentation" />
          <ChecklistItem field="titlePresent" label="Title present" description="If applicable for this transaction" />
          <ChecklistItem field="inspectionStickerPresent" label="State inspection sticker" description="If applicable" />
        </div>
        <div className="form-group">
          <label className="form-label">License Plate Number</label>
          <input type="text" className="form-input" placeholder="e.g. ABC-1234"
            value={formData.licensePlate || ''}
            onChange={(e) => updateFormData('licensePlate', e.target.value.toUpperCase())} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Registration Expires</label>
            <input type="date" className="form-input" value={formData.registrationExpiry || ''}
              onChange={(e) => updateFormData('registrationExpiry', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Insurance Expires</label>
            <input type="date" className="form-input" value={formData.insuranceExpiry || ''}
              onChange={(e) => updateFormData('insuranceExpiry', e.target.value)} />
          </div>
        </div>
        <PhotoCapture field="docs_photos" label="Documents"
          instructions="Photograph registration, insurance card, and any other documents" />
        <div className="form-group">
          <label className="form-label">Documentation Notes</label>
          <textarea className="form-input" rows={2}
            placeholder="Note any missing, expired, or unusual documentation..."
            value={formData.documentationNotes || ''}
            onChange={(e) => updateFormData('documentationNotes', e.target.value)}
            style={{ resize: 'vertical' }} />
        </div>
      </div>
      <button onClick={handleContinue} className="btn btn-primary full-width" disabled={isLoading}>
        {isLoading ? 'Saving...' : 'Continue to Exterior'} <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  );

  // ── Step 6: Exterior ──────────────────────────────────────────────────
  const PANELS = [
    'Hood', 'Roof', 'Trunk / Tailgate', 'Front Bumper', 'Rear Bumper',
    'Driver Front Door', 'Driver Rear Door', 'Passenger Front Door', 'Passenger Rear Door',
    'Driver Front Fender', 'Driver Rear Quarter', 'Passenger Front Fender', 'Passenger Rear Quarter',
    'Windshield', 'Rear Glass',
    'Driver Front Wheel', 'Driver Rear Wheel', 'Passenger Front Wheel', 'Passenger Rear Wheel',
  ];

  const renderExteriorStep = () => (
    <div className="step-content fade-in">
      <div className="step-header">
        <Eye className="w-8 h-8 text-blue-600" />
        <h2>Exterior Inspection</h2>
        <p>Photograph and assess all exterior panels</p>
      </div>
      <div className="form-container">
        <div className="instruction-card">
          <div className="instruction-icon"><Camera className="w-6 h-6 text-blue-600" /></div>
          <div>
            <h3>Photo Instructions</h3>
            <p>Take 4 angle photos first, then note damage per panel.</p>
          </div>
        </div>
        <div>
          <p className="form-label">Required Angle Photos</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            {[
              { field: 'ext_front', label: 'Front', instructions: 'Stand 10ft in front of the vehicle, centered' },
              { field: 'ext_rear', label: 'Rear', instructions: 'Stand 10ft behind the vehicle, centered' },
              { field: 'ext_driver', label: 'Driver Side', instructions: 'Full side profile from 10ft away' },
              { field: 'ext_passenger', label: 'Passenger Side', instructions: 'Full side profile from 10ft away' },
            ].map(({ field, label, instructions }) => {
              const photos = formData[field] || [];
              const isUploading = uploadingField === field;
              return (
                <button key={field} type="button"
                  className="photo-capture-button"
                  onClick={() => openCamera(field, label, instructions)}
                  disabled={isUploading}
                  style={{ flexDirection: 'column', gap: '0.4rem', padding: '1rem', aspectRatio: '4/3', position: 'relative' }}>
                  {photos.length > 0
                    ? <img src={photos[photos.length - 1]} alt={label}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'var(--radius-sm)', opacity: 0.85 }} />
                    : null}
                  <Camera className="w-6 h-6" style={{ position: 'relative', zIndex: 1 }} />
                  <span style={{ fontSize: '0.8rem', position: 'relative', zIndex: 1 }}>
                    {photos.length > 0 ? `✓ ${label}` : label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <p className="form-label">Panel Condition</p>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
            Mark each panel: OK (no damage) or DMG (damage present).
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {PANELS.map(panel => {
              const key = 'panel_' + panel.toLowerCase().replace(/[^a-z]/g, '_');
              return (
                <div key={panel} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.75rem 1rem', background: 'var(--surface-alt)',
                  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{panel}</span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {['ok', 'damage'].map(val => (
                      <button key={val} type="button"
                        onClick={() => updateFormData(key, val)}
                        style={{
                          padding: '0.25rem 0.75rem', border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                          background: formData[key] === val
                            ? val === 'ok' ? '#166534' : '#dc2626'
                            : 'var(--surface)',
                          color: formData[key] === val ? 'white' : 'var(--text-secondary)',
                          fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer',
                          fontFamily: 'inherit', transition: 'all 0.15s ease',
                        }}>
                        {val === 'ok' ? 'OK' : 'DMG'}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <PhotoCapture field="ext_damage_photos" label="Damage"
          instructions="Photograph each area of damage up close" />
        <div className="form-group">
          <label className="form-label">Exterior Damage Notes</label>
          <textarea className="form-input" rows={3}
            placeholder="Describe any damage in detail — location, type, severity..."
            value={formData.exteriorNotes || ''}
            onChange={(e) => updateFormData('exteriorNotes', e.target.value)}
            style={{ resize: 'vertical' }} />
        </div>
      </div>
      <button onClick={handleContinue} className="btn btn-primary full-width" disabled={isLoading}>
        {isLoading ? 'Saving...' : 'Continue to Interior'} <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  );

  // ── Step 7: Interior ──────────────────────────────────────────────────
  const renderInteriorStep = () => (
    <div className="step-content fade-in">
      <div className="step-header">
        <Car className="w-8 h-8 text-blue-600" />
        <h2>Interior Inspection</h2>
        <p>Inspect and photograph the vehicle interior</p>
      </div>
      <div className="form-container">
        <div className="instruction-card">
          <div className="instruction-icon"><Camera className="w-6 h-6 text-blue-600" /></div>
          <div>
            <h3>Photo Instructions</h3>
            <p>Photograph dashboard, front seats, rear seats, and any damage.</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          {[
            { field: 'int_dashboard', label: 'Dashboard' },
            { field: 'int_front_seats', label: 'Front Seats' },
            { field: 'int_rear_seats', label: 'Rear Seats' },
            { field: 'int_cargo', label: 'Cargo Area' },
          ].map(({ field, label }) => {
            const photos = formData[field] || [];
            const isUploading = uploadingField === field;
            return (
              <button key={field} type="button" className="photo-capture-button"
                onClick={() => openCamera(field, label, `Photograph the vehicle ${label.toLowerCase()}`)}
                disabled={isUploading}
                style={{ flexDirection: 'column', gap: '0.4rem', padding: '1rem', aspectRatio: '4/3', position: 'relative' }}>
                {photos.length > 0
                  ? <img src={photos[photos.length - 1]} alt={label}
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'var(--radius-sm)', opacity: 0.85 }} />
                  : null}
                <Camera className="w-6 h-6" style={{ position: 'relative', zIndex: 1 }} />
                <span style={{ fontSize: '0.8rem', position: 'relative', zIndex: 1 }}>
                  {photos.length > 0 ? `✓ ${label}` : label}
                </span>
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <p className="form-label">Interior Condition</p>
          <ConditionSelector field="driverSeat" label="Driver seat (tears, stains, wear)" />
          <ConditionSelector field="passengerSeat" label="Passenger seat" />
          <ConditionSelector field="rearSeats" label="Rear seats" />
          <ConditionSelector field="headliner" label="Headliner (stains, sags, tears)" />
          <ConditionSelector field="carpet" label="Carpet and floor mats" />
          <ConditionSelector field="dashboard" label="Dashboard (cracks, warning lights)" />
          <ConditionSelector field="infotainment" label="Infotainment / stereo system" />
          <ConditionSelector field="sunroof" label="Sunroof / moonroof" />
        </div>
        <div className="form-group">
          <label className="form-label">Odor Assessment</label>
          <select className="form-input" value={formData.odor || ''}
            onChange={(e) => updateFormData('odor', e.target.value)}>
            <option value="">Select...</option>
            <option value="none">No odor</option>
            <option value="mild">Mild odor</option>
            <option value="smoke">Smoke</option>
            <option value="pet">Pet odor</option>
            <option value="mold">Mold / mildew</option>
            <option value="other">Other</option>
          </select>
        </div>
        <PhotoCapture field="int_damage_photos" label="Interior Damage"
          instructions="Photograph any stains, tears, or damage up close" />
        <div className="form-group">
          <label className="form-label">Interior Notes</label>
          <textarea className="form-input" rows={3}
            placeholder="Describe any damage, missing items, or notable conditions..."
            value={formData.interiorNotes || ''}
            onChange={(e) => updateFormData('interiorNotes', e.target.value)}
            style={{ resize: 'vertical' }} />
        </div>
      </div>
      <button onClick={handleContinue} className="btn btn-primary full-width" disabled={isLoading}>
        {isLoading ? 'Saving...' : 'Continue to Engine'} <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  );

  // ── Step 8: Engine ────────────────────────────────────────────────────
  const renderEngineStep = () => (
    <div className="step-content fade-in">
      <div className="step-header">
        <Wrench className="w-8 h-8 text-blue-600" />
        <h2>Engine Compartment</h2>
        <p>Inspect the engine bay and fluid levels</p>
      </div>
      <div className="form-container">
        <div className="instruction-card">
          <div className="instruction-icon"><Camera className="w-6 h-6 text-blue-600" /></div>
          <div>
            <h3>Photo Instructions</h3>
            <p>Take a wide shot of the full engine bay, then close-ups of any issues.</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          {[
            { field: 'eng_wide', label: 'Engine Bay (Wide)', instructions: 'Step back to capture the full engine bay' },
            { field: 'eng_close', label: 'Engine Bay (Detail)', instructions: 'Close-up of engine block and components' },
          ].map(({ field, label, instructions }) => {
            const photos = formData[field] || [];
            const isUploading = uploadingField === field;
            return (
              <button key={field} type="button" className="photo-capture-button"
                onClick={() => openCamera(field, label, instructions)}
                disabled={isUploading}
                style={{ flexDirection: 'column', gap: '0.4rem', padding: '1rem', aspectRatio: '4/3', position: 'relative' }}>
                {photos.length > 0
                  ? <img src={photos[photos.length - 1]} alt={label}
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'var(--radius-sm)', opacity: 0.85 }} />
                  : null}
                <Camera className="w-6 h-6" style={{ position: 'relative', zIndex: 1 }} />
                <span style={{ fontSize: '0.8rem', position: 'relative', zIndex: 1, textAlign: 'center' }}>
                  {photos.length > 0 ? `✓ ${label}` : label}
                </span>
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <p className="form-label">Fluid Levels</p>
          <ConditionSelector field="engineOil" label="Engine oil (level and condition)" />
          <ConditionSelector field="coolant" label="Coolant / antifreeze" />
          <ConditionSelector field="brakeFluid" label="Brake fluid" />
          <ConditionSelector field="powerSteering" label="Power steering fluid" />
          <ConditionSelector field="washerFluid" label="Windshield washer fluid" />
          <p className="form-label" style={{ marginTop: '0.5rem' }}>Engine Bay Condition</p>
          <ConditionSelector field="engineLeaks" label="No visible fluid leaks" />
          <ConditionSelector field="beltsHoses" label="Belts and hoses (no cracks/fraying)" />
          <ConditionSelector field="battery" label="Battery condition" />
          <ConditionSelector field="airFilter" label="Air filter (visible condition)" />
        </div>
        <div className="form-group">
          <label className="form-label">Overall Engine Condition</label>
          <select className="form-input" value={formData.engineCondition || ''}
            onChange={(e) => updateFormData('engineCondition', e.target.value)}>
            <option value="">Select overall condition...</option>
            <option value="excellent">Excellent — clean, no issues</option>
            <option value="good">Good — minor wear, no leaks</option>
            <option value="fair">Fair — some issues noted</option>
            <option value="poor">Poor — significant issues</option>
          </select>
        </div>
        <PhotoCapture field="eng_issue_photos" label="Engine Issues"
          instructions="Photograph any leaks, damage, or concerning areas" />
        <div className="form-group">
          <label className="form-label">Engine Notes</label>
          <textarea className="form-input" rows={3}
            placeholder="Note any leaks, unusual sounds, warning lights, or damage..."
            value={formData.engineNotes || ''}
            onChange={(e) => updateFormData('engineNotes', e.target.value)}
            style={{ resize: 'vertical' }} />
        </div>
      </div>
      <button onClick={handleContinue} className="btn btn-primary full-width" disabled={isLoading}>
        {isLoading ? 'Saving...' : 'Continue to Final Review'} <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  );

  // ── Router ────────────────────────────────────────────────────────────
  const renderStep = () => {
    switch (currentStep) {
      case 'vehicle-info':    return renderVehicleInfoStep();
      case 'bol':             return renderBOLStep();
      case 'keys':            return renderKeysStep();
      case 'function':        return renderFunctionStep();
      case 'documentation':   return renderDocumentationStep();
      case 'exterior':        return renderExteriorStep();
      case 'interior':        return renderInteriorStep();
      case 'engine':          return renderEngineStep();
      default:                return null;
    }
  };

  return (
    <>
      {cameraOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              onClick={() => { setCameraOpen(false); setCameraConfig(null); }}
              style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: 4 }}>
              <X className="w-6 h-6" />
            </button>
            <span style={{ color: 'white', fontWeight: 600 }}>{cameraConfig?.label}</span>
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <CameraCapture
              instructions={cameraConfig?.instructions}
              onCapture={handlePhotoCapture}
              onCancel={() => { setCameraOpen(false); setCameraConfig(null); }}
            />
          </div>
        </div>
      )}
      {renderStep()}
    </>
  );
};

export default InspectionWizard;
